import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Leaf, Heart, LogOut, Settings, Package, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Header = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/?search=${searchTerm}`);
    }
  };

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    navigate('/');
  };

  return (
    <header className="bg-white border-b border-primary-100 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <Leaf className="text-primary-600 h-8 w-8" />
          <span className="text-2xl font-bold text-primary-900 tracking-tight">Alaiaathi</span>
        </Link>

        <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-primary-50 border-none rounded-full py-2 pl-10 pr-4 focus:ring-2 focus:ring-primary-500 outline-none transition-all"
            />
            <Search className="absolute left-3 top-2.5 text-primary-400 h-5 w-5" />
          </div>
        </form>

        <nav className="flex items-center space-x-5">
          {user && (
            <Link to="/favorites" className="relative text-secondary-600 hover:text-red-500 transition-colors" title="Favorites">
              <Heart className="h-6 w-6" />
            </Link>
          )}

          <Link to="/cart" className="relative text-secondary-600 hover:text-primary-600 transition-colors" title="Cart">
            <ShoppingCart className="h-6 w-6" />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {cartCount}
              </span>
            )}
          </Link>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-2 text-secondary-700 hover:text-primary-600 transition-colors"
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold text-sm">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:inline font-medium text-sm">{user.name.split(' ')[0]}</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {showDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
                  <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-2xl border border-secondary-100 py-2 z-50">
                    <div className="px-4 py-3 border-b border-secondary-50">
                      <p className="font-bold text-secondary-900 text-sm">{user.name}</p>
                      <p className="text-xs text-secondary-400">{user.email}</p>
                    </div>
                    <Link to="/profile" onClick={() => setShowDropdown(false)} className="flex items-center space-x-3 px-4 py-3 hover:bg-primary-50 text-secondary-700 transition-colors">
                      <Settings className="h-4 w-4" /> <span className="text-sm font-medium">My Profile</span>
                    </Link>
                    <Link to="/orders" onClick={() => setShowDropdown(false)} className="flex items-center space-x-3 px-4 py-3 hover:bg-primary-50 text-secondary-700 transition-colors">
                      <Package className="h-4 w-4" /> <span className="text-sm font-medium">Order History</span>
                    </Link>
                    <Link to="/favorites" onClick={() => setShowDropdown(false)} className="flex items-center space-x-3 px-4 py-3 hover:bg-primary-50 text-secondary-700 transition-colors">
                      <Heart className="h-4 w-4" /> <span className="text-sm font-medium">Favorites</span>
                    </Link>
                    {user.isAdmin && (
                      <Link to="/admin/dashboard" onClick={() => setShowDropdown(false)} className="flex items-center space-x-3 px-4 py-3 hover:bg-primary-50 text-primary-700 transition-colors border-t border-secondary-50">
                        <User className="h-4 w-4" /> <span className="text-sm font-bold">Admin Panel</span>
                      </Link>
                    )}
                    <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 text-red-500 transition-colors border-t border-secondary-50">
                      <LogOut className="h-4 w-4" /> <span className="text-sm font-medium">Logout</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link to="/login" className="flex items-center space-x-1 text-secondary-600 hover:text-primary-600 transition-colors">
              <User className="h-6 w-6" />
              <span className="hidden sm:inline font-medium">Login</span>
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
